<script setup>
import TransportSummaryExport from './TransportSummaryExport.vue'
import TransportSummaryDetailsTable from './TransportSummaryDetailsTable.vue'

import { ref, defineProps } from 'vue'
import { formatCurrency, formatDecimals } from '@/helpers/stringHelpers'

defineProps({
  summary: {
    type: Object,
    required: true
  },
  transportList: {
    type: Array,
    default: () => []
  },
  singleFreight: {
    type: Boolean,
    default: false
  },
  freightFilterInfo: {
    type: Object,
    default: null
  },
  freightCode: {
    type: Function,
    required: true
  }
})

const showDetails = ref(false)

function formatDockets(value) {
  return value ?? '-'
}
</script>

<template>
  <v-card class="mb-3">
    <v-card-title class="d-flex align-center">
      <v-row no-gutters>
        <v-col cols="12" md="6" class="d-flex align-center">
          <h4 class="header-title mb-0 font-weight-bold text-light-blue">
            Transport Summary
          </h4>

          <v-spacer />

          <v-btn
            x-small
            text
            color="primary"
            @click="showDetails = !showDetails"
          >
            {{ showDetails ? 'Hide Details' : 'Show Details' }}
          </v-btn>
        </v-col></v-row
      >
    </v-card-title>

    <v-card-text>
      <v-row no-gutters class="align-end">
        <v-col cols="12" md="6">
          <v-simple-table dense>
            <thead>
              <tr class="bg-light-blue">
                <th>Type</th>
                <th class="text-end">Weight</th>
                <th class="text-end">Quantity</th>
                <th class="text-end">Dockets</th>
                <th class="text-end">Price</th>
                <th v-if="singleFreight" class="text-end">
                  Price for
                  {{ freightFilterInfo ? freightFilterInfo.code : '-' }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td class="font-weight-semibold font-italic text-start">
                  Pigs
                </td>
                <td class="text-end">
                  {{ formatDecimals(summary.pigsWeight) }}kg
                </td>
                <td class="text-end">{{ summary.pigs }}</td>
                <td class="text-end">
                  {{ formatDockets(summary.pigsDockets) }}
                </td>
                <td class="text-end">
                  {{ formatCurrency(summary.pigsPrice) }}
                </td>
                <td v-if="singleFreight" class="text-end">
                  {{ formatCurrency(summary.splitPricePigs) }}
                </td>
              </tr>

              <tr>
                <td class="font-weight-semibold font-italic text-start">
                  Cartons
                </td>
                <td class="text-end">
                  {{ formatDecimals(summary.cartonsWeight) }}kg
                </td>
                <td class="text-end">{{ summary.cartons }}</td>
                <td class="text-end">
                  {{ formatDockets(summary.cartonsDockets) }}
                </td>
                <td class="text-end">
                  {{ formatCurrency(summary.cartonsPrice) }}
                </td>
                <td v-if="singleFreight" class="text-end">
                  {{ formatCurrency(summary.splitPriceCartons) }}
                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr class="transport-summary-total">
                <td class="font-weight-bold text-start">Total</td>
                <td class="text-end">
                  {{
                    formatDecimals(summary.pigsWeight + summary.cartonsWeight)
                  }}kg
                </td>
                <td class="text-end">{{ summary.cartons + summary.pigs }}</td>
                <td class="text-end">
                  {{
                    formatDockets(summary.cartonsDockets + summary.pigsDockets)
                  }}
                </td>
                <td class="text-end">
                  {{ formatCurrency(summary.cartonsPrice + summary.pigsPrice) }}
                </td>
                <td v-if="singleFreight" class="text-end">
                  {{
                    formatCurrency(
                      summary.splitPriceCartons + summary.splitPricePigs
                    )
                  }}
                </td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-col>

        <v-col
          v-if="singleFreight"
          cols="12"
          md="6"
          class="d-flex justify-end mt-3 mt-md-0"
        >
          <TransportSummaryExport
            :single="true"
            :transport-list="transportList"
            :summary="summary"
            :freight-code="freightCode"
          />
        </v-col>
      </v-row>

      <v-expand-transition>
        <div v-show="showDetails" class="mt-3">
          <TransportSummaryDetailsTable
            :items="transportList"
            :single-freight="singleFreight"
            :freight-filter-info="freightFilterInfo"
            :freight-code="freightCode"
          />
        </div>
      </v-expand-transition>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.transport-summary-total > td {
  border-top: 1px solid #677787 !important;
}

.font-weight-semibold {
  font-weight: 600;
}
</style>
